/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import AddTranslations from '@hmhco/i18n-react/src/AddTranslations/AddTranslations';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormHelperText from '@mui/material/FormHelperText';
import { visuallyHidden } from '@mui/utils';
import getLocaleFile from './lang';
import useStyles from './MultiselectCheckbox.styles';
import CustomPaper from './components/CustomPaper/CustomPaper';
import ListOption from './components/ListOption/ListOption';

const MultiselectCheckbox = ({
  allOptions,
  setSelectedOptions,
  selectedOptions,
  placeholder,
  actionButtonText,
  resetButtonText,
  singleOptionSelectedText,
  multipleOptionsSelectedText,
  getOptionLabel,
  getOptionKey,
  formLabelText,
  formLabelId,
  disabled,
  required,
  error,
  formHelperText,
  disableSelectAll,
  ...otherProps
}) => {
  const { classes } = useStyles();

  const [isBlurred, setIsBlurred] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const handleClosePopper = () => {
    setIsOpen(false);
    setInputValue('');
  };

  const handleOpenPopper = () => {
    setIsOpen(true);
  };

  const handleClear = () => {
    setSelectedOptions([]);
    setInputValue('');
  };

  const handleApply = () => {
    handleClosePopper();
  };

  const filter = createFilterOptions();

  return (
    <div
      className="MultiselectCheckbox"
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          handleClosePopper();
        }
      }}
    >
      <Autocomplete
        classes={{ option: classes.option }}
        disableClearable
        disablePortal
        disabled={disabled}
        filterOptions={(options, params) => {
          if (!params?.inputValue) {
            return disableSelectAll
              ? options
              : [{ all: true, key: 'all' }, ...options];
          }

          const filtered = filter(options, params);
          return filtered;
        }}
        getOptionKey={getOptionKey}
        getOptionLabel={getOptionLabel}
        id={formLabelId}
        inputValue={inputValue}
        multiple
        onBlur={() => setIsBlurred(true)}
        onChange={(_, newValue) => {
          if (newValue.find(option => option.all)) {
            setSelectedOptions(
              selectedOptions.length === allOptions.length ? [] : allOptions,
            );
          } else {
            setSelectedOptions(newValue);
          }
        }}
        onClose={(_, reason) => {
          if (reason === 'escape') {
            handleClosePopper();
          }
        }}
        onFocus={() => setIsBlurred(false)}
        onOpen={() => {
          handleOpenPopper();
        }}
        open={isOpen}
        options={allOptions}
        PaperComponent={CustomPaper}
        renderInput={({ InputLabelProps, InputProps, inputProps }) => {
          const isInputVisuallyHidden = isBlurred && selectedOptions.length;

          return (
            <FormControl
              disabled={disabled}
              error={error}
              required={required}
              sx={{ width: '100%' }}
              variant="standard"
            >
              <FormLabel
                {...InputLabelProps}
                classes={{ root: classes.disabledStyling }}
              >
                {formLabelText}
              </FormLabel>
              <OutlinedInput
                {...InputProps}
                aria-describedby={`${InputLabelProps.htmlFor}-helper-text`}
                endAdornment={
                  <InputAdornment
                    position="end"
                    onClick={() => {
                      setInputValue(isOpen ? '' : inputValue);
                      setIsOpen(!isOpen);
                    }}
                  >
                    {InputProps.endAdornment}
                  </InputAdornment>
                }
                inputProps={{
                  ...inputProps,
                  style: isInputVisuallyHidden
                    ? visuallyHidden
                    : { padding: 0 },
                }}
                onChange={e => setInputValue(e.target.value)}
                placeholder={placeholder}
              />
              {formHelperText ? (
                <FormHelperText
                  classes={{ root: classes.disabledStyling }}
                  id={`${InputLabelProps.htmlFor}-helper-text`}
                >
                  {formHelperText}
                </FormHelperText>
              ) : null}
            </FormControl>
          );
        }}
        renderOption={(props, option, { selected }) => {
          const label = option.all
            ? `${formLabelId}-all`
            : getOptionLabel(option);
          return (
            <ListOption
              allOptions={allOptions}
              label={label}
              multipleOptionsSelectedText={multipleOptionsSelectedText}
              option={option}
              optionProps={props}
              selected={selected}
              selectedOptions={selectedOptions}
              // mui bug so disabling line below https://github.com/mui/material-ui/issues/39833
              // eslint-disable-next-line react/prop-types
              key={props.key}
            />
          );
        }}
        renderTags={optionsSelected => {
          const numOptionsSelected = optionsSelected.length;
          const isInputValueVisuallyHidden =
            (!isOpen && !isBlurred) || (isOpen && !isBlurred);

          return (
            <Typography
              style={
                isInputValueVisuallyHidden ? visuallyHidden : { width: '100%' }
              }
              variant="body2"
            >
              <FormattedMessage
                id="multiselectCheckbox.selectedOptions.itemCount"
                values={{
                  multipleOptions: multipleOptionsSelectedText,
                  selectedOptionsCount: numOptionsSelected,
                  singleOption: singleOptionSelectedText,
                }}
              />
            </Typography>
          );
        }}
        slotProps={{
          listbox: {
            tabIndex: '-1', // needed to focus action buttons and not listbox scroll container on tab from within listbox
            style: { maxHeight: '15rem' },
            position: 'bottom-start',
          },
          paper: {
            actionButtonText,
            classes,
            onApply: handleApply,
            onClear: handleClear,
            resetButtonText,
            formLabelId,
          },
        }}
        value={selectedOptions}
        {...otherProps}
      />
    </div>
  );
};

MultiselectCheckbox.defaultProps = {
  disabled: false,
  required: false,
  error: false,
  formHelperText: '',
  disableSelectAll: false,
};

MultiselectCheckbox.propTypes = {
  allOptions: PropTypes.array.isRequired,
  setSelectedOptions: PropTypes.func.isRequired,
  selectedOptions: PropTypes.array.isRequired,
  placeholder: PropTypes.string.isRequired,
  actionButtonText: PropTypes.string.isRequired,
  resetButtonText: PropTypes.string.isRequired,
  singleOptionSelectedText: PropTypes.string.isRequired,
  multipleOptionsSelectedText: PropTypes.string.isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  getOptionKey: PropTypes.func.isRequired,
  formLabelText: PropTypes.string.isRequired,
  formLabelId: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.bool,
  formHelperText: PropTypes.string,
  disableSelectAll: PropTypes.bool,
};

/**
 * MultiselectCheckbox component allows users to select multiple options from a dropdown list with checkboxes.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.allOptions - The array of options to display in the MultiselectCheckbox dropdown.
 * @param {Function} props.setSelectedOptions - Callback function to update the selected options.
 * @param {Array} props.selectedOptions - The array of currently selected options.
 * @param {string} props.placeholder - Placeholder text for the input field.
 * @param {string} props.actionButtonText - Text for the action button in the custom paper component.
 * @param {string} props.resetButtonText - Text for the reset button in the custom paper component.
 * @param {string} props.singleOptionSelectedText - Text to display when a single option is selected.
 * @param {string} props.multipleOptionsSelectedText - Text to display when multiple options are selected.
 * @param {Function} props.getOptionLabel - Function that returns the label for a given option.
 * @param {Function} props.getOptionKey - Function that returns a unique key for a given option.
 * @param {string} props.formLabelText - Text for the form label.
 * @param {string} props.formLabelId - ID for the form label and input field.
 * @param {boolean} [props.disabled=false] - If true, the MultiselectCheckbox component is disabled.
 * @param {boolean} [props.required=false] - If true, the input field is required.
 * @param {boolean} [props.error=false] - If true, the input field will indicate an error state.
 * @param {string} [props.formHelperText=''] - Helper text to display below the input field.
 * @param {boolean} [props.disableSelectAll] - If true, Select All will not be an option in the dropdown.
 */
export default props => (
  <AddTranslations getLocaleFile={getLocaleFile}>
    <MultiselectCheckbox {...props} />
  </AddTranslations>
);
